import { DateRange } from "../models/DateRange";
import { ExportImportTransaction, Transaction } from "../models/Transaction";
import { IndexerService } from "./indexer.service";
import { CCHAIN_TRANSACTION_RETURN_LIMIT, CSV_DELIMITER, TRANSACTION_RETURN_LIMIT } from "../constants";
import {
  calculateAmountValue,
  calculateFees,
  calculateWeightAmount,
} from "../components/transactionDetailsTable/TransactionDetailsTable.component";
import { TransactionType } from "../models/enums";
import { convertFromGwei, convertFromWei, formatNumber } from "../utils/formatter";
import { formatToISO, useAvailableTime } from "../utils/formatTime";
import { calculateAmount } from "../utils/exportImportHelper";

const tnxAddressStructure = {
  txID: "Transaction ID",
  type: "Type",
  blockHeight: "Block Height",
  chainTime: "Timestamp",
  weight: "Amount",
  fee: "Fee", // (sum(input.amount) - sum(output.amount))
};

const cChainTnxStructure = {
  type: "Type",
  timestamp: "Timestamp",
  weightCChain: "Amount",
  feeCChain: "Fee",
};

export class TransactionExportService {

  /**
   * SafeGuard for checking if the row is of type Transaction.
   * This method helps differentiate between `Transaction` and `ExportImportTransaction` types.
   */
  static IsTnxTypeTransaction(row: Transaction | ExportImportTransaction): row is Transaction {
    return (row as Transaction).blockHeight !== undefined;
  }

  /**
   * Returns the value for a given field name from the provided row.
   * The value returned is formatted depending on the type of the row (Transaction or ExportImportTransaction).
   *
   * @param fieldName - The field name for which the value is to be retrieved.
   * @param row - The row object (either of type Transaction or ExportImportTransaction).
   *
   * @returns A string or number value based on the field requested and the row's type.
   */
  static returnValueForKey(
    fieldName: string,
    row: Transaction | ExportImportTransaction,
  ): string | number {
    // for type Transaction
    if (this.IsTnxTypeTransaction(row)) {

      if (fieldName === "fee") {
        return convertFromGwei(calculateFees(row));
      }
      if (fieldName === "weight") {
        if (
          row.type === TransactionType.ADD_VALIDATOR_TX ||
          row.type === TransactionType.ADD_DELEGATOR_TX ||
          row.type === TransactionType.ADD_PERMISSIONLESS_VALIDATOR_TX ||
          row.type === TransactionType.ADD_PERMISSIONLESS_DELEGATOR_TX
        ) {
          return convertFromGwei(calculateWeightAmount(row));
        } else {
          return convertFromGwei(calculateAmountValue(row));
        }
      }
      if (fieldName === "chainTime") {
        const timeValue: string | null = useAvailableTime(row);
        return timeValue ? timeValue : "/";
      }
    }

    // for type ExportImportTransaction
    else {
      if (fieldName === "feeCChain") {
        return formatNumber(convertFromWei(row.total_gas), false, 4);
      }

      if (fieldName === "weightCChain") {
        return formatNumber(convertFromWei(calculateAmount(row)), false, 2, true);
      }

      if (fieldName === "timestamp") {
        const timeValue: string | null = formatToISO(row.timestamp);
        return timeValue ? timeValue : "/";
      }
    }

    // returns others
    if (fieldName in row) {
      return row[fieldName as keyof typeof row];
    }
    // returns empty if not found
    return "";
  }

  static getDateToISO(date: Date): string | null {
    return new Date(date).toISOString();
  }


  static async convertToCSV(data: (Transaction | ExportImportTransaction)[], tnxStructure: object) {
    const columnNames = Object.values(tnxStructure);
    const header = columnNames.join(CSV_DELIMITER);
    const columns = Object.keys(tnxStructure);
    const rowsContent = data.map((row) => {
      const values = columns.map((fieldName) =>
        this.returnValueForKey(fieldName, row),
      );
      return values.join(CSV_DELIMITER);
    });

    const csvContent = `sep=;\n${header}\n${rowsContent.join("\n")}`;

    return csvContent;
  }

  /**
   * Fetches transactions from the P-Chain for a given address within an optional date range.
   * This method handles pagination by continuously fetching transactions until there are no more to retrieve.
   * The results are then converted into a CSV format.
   *
   * @param address - The blockchain address for which transactions are fetched.
   * @param dateRange - The optional date range to filter transactions. If null, fetches all transactions.
   * @returns A promise that resolves to a CSV string containing all fetched transactions.
   */
  static async fetchPChainTransactions(
    address: string,
    dateRange: DateRange | null = null,
  ): Promise<string> {
    const dateFrom = dateRange ? this.getDateToISO(dateRange.from) : null;
    const dateTo = dateRange ? this.getDateToISO(dateRange.to) : null;

    let offset = 0;
    const transactions: (Transaction)[] = [];

    let keepFetching = true;
    try {
      while (keepFetching) {
        const txs = await IndexerService.getTransactionsPerAddress(address, dateFrom, dateTo, offset);

        const txsData = txs.data.transactions;
        const dataLength = txs.data.transactions.length;

        transactions.push(...txsData);

        if (dataLength < TRANSACTION_RETURN_LIMIT) {
          keepFetching = false;
          break;
        }
        offset += TRANSACTION_RETURN_LIMIT;
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      throw error;
    }
    return this.convertToCSV(transactions, tnxAddressStructure);
  }

  /**
   * Fetches transactions for a given address within a specified date range from the C-Chain.
   * This method supports pagination and retrieves all transactions until no more are available.
   * Transactions are returned in CSV format.
   *
   * @param address - The blockchain address for which transactions are fetched.
   * @param dateRange - The optional date range to filter transactions. If null, fetches all transactions.
   * @param limit - The maximum number of transactions per request.
   * @returns A promise that resolves to a CSV string containing all fetched transactions.
   */
  static async fetchCChainTransactions(
    address: string,
    dateRange: DateRange | null = null,
    limit: number,
  ): Promise<string> {
    const dateFrom = dateRange ? (Math.floor(dateRange.from.getTime() / 1000)) : null;
    const dateTo = dateRange ? (Math.floor(dateRange.to.getTime() / 1000)) : null;

    let offset = 0;
    const transactions: (Transaction | ExportImportTransaction)[] = [];

    let keepFetching = true;
    try {
      while (keepFetching) {
        const txs = await IndexerService.getCChainTransactions(address, dateFrom, dateTo, offset, limit);

        const txsData = txs.results;
        const dataLength = txs.results.length;

        transactions.push(...txsData);

        if (dataLength < CCHAIN_TRANSACTION_RETURN_LIMIT) {
          keepFetching = false;
          break;
        }
        offset += CCHAIN_TRANSACTION_RETURN_LIMIT;
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      throw error;
    }
    return this.convertToCSV(transactions, cChainTnxStructure);
  }

}
